<template>
  <div>
    <div
      v-for="group in groups"
      :key="group.key"
      class="border rounded px-1 pt-1 mb-1"
    >
      <h6 class="d-flex align-items-center mb-1">
        <feather-icon
          :icon="group.icon"
          size="16"
          class="text-primary"
        />
        <span class="mx-50">{{ $t(group.title) }}</span>
      </h6>
      <b-row>
        <b-col
          v-for="field in group.fields"
          :key="field.key"
          cols="12"
          sm="6"
        >
          <b-form-group
            v-if="field.type === 'date'"
            :label="$t(field.label)"
            :label-for="field.key"
          >
            <validation-provider
              v-slot="{ errors }"
              :name="$t(field.label)"
              rules="required"
            >
              <date-picker
                :id="field.key"
                :value="value[field.key]"
                type="date"
                :color="getColor('primary')"
                :locale="$i18n.locale === 'fa' ? 'fa' : 'en'"
                :format="dateFormat"
                :display-format="$i18n.locale === 'fa' ? 'jYYYY/jMM/jDD' : 'YYYY-MM-DD'"
                :min="field.min"
                :placeholder="$t(field.label)"
                auto-submit
                @input="update(field.key, $event)"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group v-else>
            <validation-provider
              v-slot="{ errors }"
              :name="$t(field.label)"
              :rules="field.rules"
            >
              <label :for="field.key">
                <span>{{ $t(field.label) }}</span>
                <feather-icon
                  :id="`popover-${field.key}`"
                  icon="HelpCircleIcon"
                  size="18"
                  style="margin: 0 4px 4px;"
                  href="#"
                  tabindex="0"
                />
                <b-popover
                  :target="`popover-${field.key}`"
                  variant="danger"
                  placement="top"
                  triggers="focus"
                >
                  <span>{{ $t(`${field.label}_description`) }}</span>
                </b-popover>
              </label>
              <b-form-input
                :id="field.key"
                :value="value[field.key]"
                type="number"
                step="any"
                min="0"
                number
                :name="field.key"
                :placeholder="$t(field.label)"
                autocomplete="nope"
                @update="update(field.key, $event)"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>
        <b-col
          v-if="group.key === 'trade' && !isDateOrderValid"
          cols="12"
          class="mb-1"
        >
          <small class="text-danger">{{ $t('pamm_period_date_order_error') }}</small>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BPopover,
} from 'bootstrap-vue'
import DatePicker from 'vue-persian-datetime-picker'
import { ValidationProvider } from 'vee-validate'
import { required, between } from '@validations'
import moment from '@/libs/moment'
import { PERIOD_DATE_FORMAT, isPeriodDateOrderValid } from '@/modules/pamm/utils/period'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BPopover,
    DatePicker,
    ValidationProvider,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      required,
      between,
      dateFormat: PERIOD_DATE_FORMAT,
      today: moment().format(PERIOD_DATE_FORMAT),
    }
  },
  computed: {
    groups() {
      return [
        {
          key: 'invest',
          title: 'pamm_invest_window',
          icon: 'CalendarIcon',
          fields: [
            {
              key: 'invest_start_at', label: 'pamm_invest_start_at', type: 'date', min: this.minDate('invest_start_at'),
            },
            {
              key: 'invest_end_at', label: 'pamm_invest_end_at', type: 'date', min: this.minDate('invest_end_at', 'invest_start_at'),
            },
          ],
        },
        {
          key: 'trade',
          title: 'pamm_trade_window',
          icon: 'TrendingUpIcon',
          fields: [
            {
              key: 'trade_start_at', label: 'pamm_trade_start_at', type: 'date', min: this.minDate('trade_start_at', 'invest_end_at'),
            },
            {
              key: 'trade_end_at', label: 'pamm_trade_end_at', type: 'date', min: this.minDate('trade_end_at', 'trade_start_at'),
            },
          ],
        },
        {
          key: 'fees',
          title: 'pamm_terms_fees',
          icon: 'DollarSignIcon',
          fields: [
            { key: 'fee_static', label: 'fee_static', rules: 'required' },
            { key: 'fee_percent', label: 'fee_percent', rules: 'required|between:0,100' },
          ],
        },
        {
          key: 'sharing',
          title: 'pamm_terms_sharing',
          icon: 'PieChartIcon',
          fields: [
            { key: 'profit_share_percent', label: 'profit_share', rules: 'required|between:0,100' },
            { key: 'loss_share_percent', label: 'loss_share', rules: 'required|between:0,100' },
          ],
        },
        {
          key: 'cancelation',
          title: 'pamm_terms_cancelation',
          icon: 'ClockIcon',
          fields: [
            { key: 'premature_cancelation_period', label: 'caneclation_period', rules: 'required' },
            { key: 'premature_cancelation_fine_percent', label: 'caneclation_fee', rules: 'required|between:0,100' },
          ],
        },
      ]
    },
    isDateOrderValid() {
      return isPeriodDateOrderValid(this.value)
    },
  },
  methods: {
    // the picker replaces values below min, so a saved past date (when editing) must stay selectable
    minDate(key, previousKey) {
      if (previousKey && this.value[previousKey]) return this.value[previousKey]
      const current = this.value[key]
      return current && moment(current).isBefore(this.today) ? current : this.today
    },
    update(key, fieldValue) {
      this.$emit('input', { ...this.value, [key]: fieldValue })
    },
  },
}
</script>
